/// Multiplayer protocol payloads for Soundings — sent over the
/// generic `@nav-games/multiplayer` Peer transport. The protocol is
/// turn-based: HOST fires first, alternates with GUEST, each fire
/// gets one result back. Grounding (hitting a rock on the
/// opponent's chart) costs the firer a life and ends the turn;
/// hitting water / cardinal / ship just ends the turn normally.
///
/// Both players run their own copy of the random board so neither
/// side leaks ship positions — only the FIRE_RESULT messages are
/// exchanged.

import type { Direction } from "./code";

export type MultiMessageType = "ready" | "fire" | "fire-result";

/// Sent once a player has finished arranging their fleet in the
/// placement phase. Play starts when both players are ready.
export interface ReadyMessage {
  type: "ready";
}

/// A player fires at a coordinate on the opponent's board.
export interface FireMessage {
  type: "fire";
  col: number;
  row: number;
}

/// The opponent reports what was at that coordinate on their board.
export interface FireResultMessage {
  type: "fire-result";
  col: number;
  row: number;
  /// Mirror of `RevealResult.event` minus the housekeeping events
  /// (already-revealed, game-over) — those never go on the wire.
  event: "water" | "cardinal" | "hit" | "sunk" | "grounded";
  /// Direction letter when `event === "cardinal"`.
  cardinalDirection?: Direction;
  /// Ship display name when `event === "sunk"`.
  sunkShipName?: string;
  /// Defender's remaining lives after the shot (so the attacker
  /// can decide whether the opponent's run aground is endangering
  /// their game).
  defenderLivesRemaining: number;
  /// True when this shot sank the defender's last ship — game
  /// over, attacker wins.
  fleetSunk: boolean;
}

export type MultiMessage = ReadyMessage | FireMessage | FireResultMessage;

export function isMultiMessage(value: unknown): value is MultiMessage {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.type !== "string") return false;
  if (v.type === "ready") return true;
  if (v.type === "fire") {
    return typeof v.col === "number" && typeof v.row === "number";
  }
  if (v.type === "fire-result") {
    return (
      typeof v.col === "number" &&
      typeof v.row === "number" &&
      typeof v.event === "string" &&
      typeof v.defenderLivesRemaining === "number" &&
      typeof v.fleetSunk === "boolean"
    );
  }
  return false;
}
